import { useLocation, useNavigate } from 'react-router-dom';
import { SortBy, SortOrderBy } from '../types';

type View = 'list' | 'grid';
type QueryParams = Partial<{
  token: string;
  emailVerification: string;
  view: View;
  sortBy: SortBy;
  sortOrder: SortOrderBy;
  searchQuery: string;
  errorCode: string;
  errorTitle: string;
  errorDescription: string;
  redirectUrl: string;
  referrerUrl: string;
  intent: string;
  joinCode: string;
  zoomCode: string; // Added zoomCode
  state: string;
  importUrl: string; // If this is present, we will automatically import this video from the url
  teamId: string; // The team id for settings page overrides
  creatorId: string; // Add this field
  showTrial: string;
  importFlow: string;
  openUpgrade: string;
  source: string;
  returnTo: string;
  anonToken: string;
  anonId: string;
  dialog: string; // URL-based dialog/popup identifier
}>;

const extractJoinCodeFromUrl = (url: string): string | undefined => {
  // Extract join code from URL, handling both direct URLs and redirect URLs
  try {
    const urlObj = new URL(url);

    // Check query parameters directly
    const joinCodeFromQuery = urlObj.searchParams.get('joinCode');
    if (joinCodeFromQuery) {
      return joinCodeFromQuery;
    }

    // Check if there's a hash part with query parameters
    if (urlObj.hash) {
      const hashPart = urlObj.hash.substring(1); // Remove the # character
      // Parse the hash part as query parameters
      const hashParams = new URLSearchParams(
        hashPart.includes('?') ? hashPart.split('?')[1] : '',
      );
      const joinCodeFromHash = hashParams.get('joinCode');
      if (joinCodeFromHash) {
        return joinCodeFromHash;
      }
    }

    return undefined;
  } catch {
    // If URL parsing fails, try to extract join code using regex as fallback
    const joinCodeMatch = url.match(/[?&]joinCode=([^&]+)/);
    return joinCodeMatch ? decodeURIComponent(joinCodeMatch[1]) : undefined;
  }
};

export const useQuery = (): Partial<QueryParams> => {
  // Get the query parameters from the URL. This can be used anywhere in the app
  const location = useLocation();

  // For HashRouter, we need to check both:
  // 1. Query params after the hash: /#/path?query=value (location.search)
  // 2. Query params before the hash: /?query=value#/path (window.location.search)
  const hashQueryParams = new URLSearchParams(location.search);
  const windowQueryParams = new URLSearchParams(window.location.search);

  // Merge both, with hash params taking precedence
  const queryParams = new URLSearchParams();

  // First add window query params (before hash)
  windowQueryParams.forEach((value, key) => {
    queryParams.set(key, value);
  });

  // Then add/override with hash query params (after hash)
  hashQueryParams.forEach((value, key) => {
    queryParams.set(key, value);
  });

  // Utility function to conditionally include query params
  const includeParam = <T>(key: string, fallbackValue?: T): T | undefined => {
    const value = queryParams.get(key);
    return value !== null ? (value as unknown as T) : fallbackValue;
  };

  const result: Partial<QueryParams> = {};

  // Mapping of keys to their respective default or fallback values
  const queryMap: { [key in keyof QueryParams]: () => any } = {
    token: () => includeParam<string>('token'),
    emailVerification: () => includeParam<string>('emailVerification'),
    view: () => includeParam<View>('view'),
    sortBy: () => includeParam<SortBy>('sortBy'),
    sortOrder: () => includeParam<SortOrderBy>('sortOrder'),
    searchQuery: () => includeParam<string>('searchQuery'),
    errorCode: () => includeParam<string>('errorCode'),
    errorTitle: () => includeParam<string>('errorTitle'),
    errorDescription: () => includeParam<string>('errorDescription'),
    redirectUrl: () => includeParam<string>('redirectUrl'),
    referrerUrl: () => includeParam<string>('referrerUrl'),
    intent: () => includeParam<string>('intent'),
    openUpgrade: () => includeParam<string>('openUpgrade'),
    source: () => includeParam<string>('source'),
    returnTo: () => includeParam<string>('returnTo'),
    joinCode: () => {
      // First check current URL for joinCode
      const directJoinCode = includeParam<string>('joinCode');
      if (directJoinCode) {
        return directJoinCode;
      }

      // If no direct joinCode, check if redirectUrl contains one
      const redirectUrl = includeParam<string>('redirectUrl');
      if (redirectUrl) {
        return extractJoinCodeFromUrl(redirectUrl);
      }

      return undefined;
    },
    zoomCode: () => includeParam<string>('zoomCode'), // Added zoomCode handling
    state: () => includeParam<string>('state'),
    importUrl: () => includeParam<string>('importUrl'),
    teamId: () => includeParam<string>('teamId'),
    creatorId: () => includeParam<string>('creatorId'), // Add this mapping
    showTrial: () => includeParam<string>('showTrial'),
    importFlow: () => includeParam<string>('importFlow'),
    anonToken: () => includeParam<string>('anonToken'),
    anonId: () => includeParam<string>('anonId'),
    dialog: () => includeParam<string>('dialog'),
  };

  // Iterate over the queryMap to populate the result object with non-null values
  Object.keys(queryMap).forEach((key) => {
    const value = queryMap[key as keyof QueryParams]?.();
    if (value !== undefined && value !== '') {
      result[key as keyof QueryParams] = value;
    }
  });

  return result;
};

export const useUpdateQuery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search } = location;
  const queryParams = new URLSearchParams(search);

  const updateQuery = (params: QueryParams) => {
    // To remove a query param, set it to undefined
    Object.keys(params).forEach((key: keyof QueryParams) => {
      const value = params[key];
      if (key && value) {
        queryParams.set(key, value);
      } else {
        queryParams.delete(key);
      }
    });

    const searchParamsString = queryParams.toString();
    navigate({
      pathname,
      search: searchParamsString ? `?${searchParamsString}` : '', // Conditionally add `?`
    });
  };

  return updateQuery;
};

export const useAuthQueryParams = () => {
  // Get all the valid query parameters from the URL and return them as a string
  // which can be appended to the URL when redirecting
  const queryParams = useQuery();
  const query = new URLSearchParams(queryParams).toString();
  const queryString = query ? `?${query}` : '';

  return queryString;
};

export default useQuery;
